import { buildBusinessCaseText } from "./BusinessCasePreview";
import { assumptionLabels } from "./defaults";
import { formatCurrencyGbp, formatNumber, ukDate } from "./calculations";

const slugify = (input) =>
  String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const humanAssumptions = (values) =>
  Object.entries(values || {}).map(([key, value]) => [assumptionLabels[key] || key, String(value ?? "")]);

export const buildPdfModel = (values, calc) => {
  const company = values.companyName || "company";
  const role = values.roleBeingRecruited || values.campaignName || "role";
  const location = values.depotLocation || "Not specified";
  const feePerHire = Number(values.feePerSuccessfulHire || 350);
  const successHires = Number(values.successfulRailwayHires || 0);

  return {
    filename: `${slugify(company)}-${slugify(role)}-talent-pool-business-case.pdf`,
    cover: {
      company,
      role,
      location,
      preparedBy: values.preparedBy || "Not specified",
      decisionDate: ukDate(values.targetDecisionDate),
      vacancies: formatNumber(values.vacanciesToFill, 0),
      successfulRailwayHires: formatNumber(successHires, 0),
      feePerHire: formatCurrencyGbp(feePerHire, 0),
      totalRailwayFee: formatCurrencyGbp(calc?.summary?.railwayCareersSuccessFee, 0),
      internalCapacityReleased: `${formatNumber(calc?.summary?.internalCapacityReleasedHours, 1)} hours`,
      indicativeHoursValue: formatCurrencyGbp(calc?.summary?.internalCapacityValue, 0),
      talentPoolCostPerCampaign: formatCurrencyGbp(calc?.summary?.talentPoolCostPerCampaign, 0),
      netModelledValue: formatCurrencyGbp(calc?.summary?.perCampaignNetValue, 0),
      decisionRequested: values.decisionRequested || "Not specified",
      commercialLine: "No access fee. No subscription. £350 only when a candidate is successfully hired.",
    },
    narrative: buildBusinessCaseText(values, calc),
    assumptions: humanAssumptions(values),
    metrics: {
      conventionalCost: calc?.conventional?.totalModelledCost || 0,
      talentCost: calc?.talent?.totalModelledCost || 0,
      conventionalCostPerHire: calc?.conventional?.costPerSuccessfulHire || 0,
      talentCostPerHire: calc?.talent?.costPerSuccessfulHire || 0,
      railwayFee: calc?.summary?.railwayCareersSuccessFee || 0,
    },
  };
};

const loadPdfDeps = async () => {
  const [jspdfModule, autoTableModule] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  return {
    jsPDF: jspdfModule.default,
    autoTable: autoTableModule.default || autoTableModule,
  };
};

export const downloadBusinessCasePdf = async (values, calc) => {
  const { jsPDF, autoTable } = await loadPdfDeps();
  const model = buildPdfModel(values, calc);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFillColor(11, 27, 61);
  doc.rect(0, 0, pageWidth, 100, "F");
  doc.setTextColor(255, 184, 0);
  doc.setFontSize(12);
  doc.text("Railway Careers UK", 40, 36);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text("Talent Pool Business Case", 40, 68);

  doc.setTextColor(15, 23, 42);
  autoTable(doc, {
    startY: 118,
    head: [["Cover summary", "Value"]],
    body: [
      ["Company", model.cover.company],
      ["Role and location", `${model.cover.role} · ${model.cover.location}`],
      ["Prepared by", model.cover.preparedBy],
      ["Target decision date", model.cover.decisionDate],
      ["Vacancies", model.cover.vacancies],
      ["Successful Railway Careers UK hires", model.cover.successfulRailwayHires],
      ["Commercial model", model.cover.commercialLine],
      ["Fee per successful hire", model.cover.feePerHire],
      ["Total Railway Careers UK success fee", model.cover.totalRailwayFee],
      ["Internal capacity released", model.cover.internalCapacityReleased],
      ["Indicative value of released hours", model.cover.indicativeHoursValue],
      ["Talent Pool cost per campaign", model.cover.talentPoolCostPerCampaign],
      ["Net modelled value", model.cover.netModelledValue],
      ["Decision requested", model.cover.decisionRequested],
    ],
    theme: "grid",
    styles: { fontSize: 9.5 },
    headStyles: { fillColor: [11, 27, 61] },
    columnStyles: { 0: { cellWidth: 190 } },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 14,
    head: [["Funnel comparison", "Conventional route", "Talent Pool route"]],
    body: [
      ["Applications / profiles reviewed", formatNumber(values.conventionalApplicationsReceived, 0), formatNumber(values.talentProfilesReviewed, 0)],
      ["Gateway pass / operational shortlist", formatNumber(values.conventionalGatewayPass, 0), formatNumber(values.talentOperationalShortlist, 0)],
      ["Interviewed", formatNumber(values.conventionalInterviewed, 0), formatNumber(values.talentInterviewed, 0)],
      ["Appointments required / successful TP hires", formatNumber(values.appointmentsRequired, 0), formatNumber(values.successfulRailwayHires, 0)],
    ],
    theme: "striped",
    styles: { fontSize: 9.5 },
    headStyles: { fillColor: [255, 184, 0], textColor: [15, 23, 42] },
  });

  doc.addPage();
  autoTable(doc, {
    startY: 50,
    head: [["Resource comparison", "Conventional", "Talent Pool"]],
    body: [
      ["Recruitment or HR hours", formatNumber(calc?.conventional?.recruitmentHours, 1), formatNumber(calc?.talent?.recruitmentHours, 1)],
      ["Operational manager hours", formatNumber(calc?.conventional?.operationalHours, 1), formatNumber(calc?.talent?.operationalHours, 1)],
      ["Other panel hours", formatNumber(calc?.conventional?.otherPanelHours, 1), formatNumber(calc?.talent?.otherPanelHours, 1)],
      ["Interview panel-days (exact)", formatNumber(calc?.conventional?.exactInterviewDays, 2), formatNumber(calc?.talent?.exactInterviewDays, 2)],
      ["Total staff-hours", formatNumber(calc?.conventional?.totalStaffHours, 1), formatNumber(calc?.talent?.totalStaffHours, 1)],
    ],
    styles: { fontSize: 9.5 },
    headStyles: { fillColor: [11, 27, 61] },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 14,
    head: [["Financial comparison", "Conventional", "Talent Pool"]],
    body: [
      ["Internal labour value", formatCurrencyGbp(calc?.conventional?.internalLabourValue, 0), formatCurrencyGbp(calc?.talent?.internalLabourValue, 0)],
      ["Confirmed cash costs", formatCurrencyGbp(calc?.conventional?.cashCosts?.totalCashCosts, 0), formatCurrencyGbp((calc?.talent?.cashCosts?.totalCashCosts || 0) - (calc?.summary?.railwayCareersSuccessFee || 0), 0)],
      ["Railway Careers UK success fee", formatCurrencyGbp(0, 0), formatCurrencyGbp(calc?.summary?.railwayCareersSuccessFee, 0)],
      ["Total modelled cost", formatCurrencyGbp(calc?.conventional?.totalModelledCost, 0), formatCurrencyGbp(calc?.talent?.totalModelledCost, 0)],
      ["Cost per appointment", formatCurrencyGbp(calc?.conventional?.costPerAppointment, 0), formatCurrencyGbp(calc?.talent?.costPerAppointment, 0)],
    ],
    styles: { fontSize: 9.5 },
    headStyles: { fillColor: [255, 184, 0], textColor: [15, 23, 42] },
  });

  doc.addPage();
  doc.setTextColor(11, 27, 61);
  doc.setFontSize(16);
  doc.text("Draft business case", 40, 46);
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(10);
  const narrative = doc.splitTextToSize(model.narrative || "", pageWidth - 80);
  doc.text(narrative, 40, 70);

  doc.addPage();
  doc.setTextColor(11, 27, 61);
  doc.setFontSize(16);
  doc.text("Assumptions appendix", 40, 46);
  autoTable(doc, {
    startY: 62,
    head: [["Assumption", "Value"]],
    body: model.assumptions,
    styles: { fontSize: 8.5 },
    headStyles: { fillColor: [11, 27, 61] },
    columnStyles: { 0: { cellWidth: 240 } },
  });

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text("No access fee. No subscription. £350 only when a candidate is successfully hired.", 40, pageHeight - 20);
    doc.text(`Page ${i} of ${pages}`, pageWidth - 78, pageHeight - 20);
  }

  doc.save(model.filename || "talent-pool-business-case.pdf");
  return model;
};
